import React from "react";
import PropTypes from "prop-types";
import { Drawer as MuiDrawer, Box, Typography } from "@mui/material";
import IconButton from "./IconButton";
import { forwardRef } from "react";

/**
 * Reusable MUI Drawer component
 * - Works for temporary (mobile) and persistent (desktop) drawers
 * - Accepts either children (fully custom content) or items (array of list items)
 * - Header actions slot and footer slot available
 *
 * Props:
 * - open (bool) : whether the drawer is open
 * - onClose (func) : function to call to close the drawer
 * - anchor (string) : left | right | top | bottom
 * - title (node|string) : title shown in header
 * - width (number|string) : width of the drawer when anchor is left/right
 * - variant (string) : 'temporary' | 'persistent' | 'permanent'
 * - headerActions (node) : extra controls on the header (e.g. search, filters)
 * - footer (node) : content fixed at bottom of drawer
 * - PaperProps (object) : forwarded to the Drawer Paper component
 */
const Drawer = forwardRef(
  (
    {
      open,
      className,
      onClose,
      anchor = "left",
      title,
      width = 320,
      variant = "temporary",
      children,
      headerActions = null,
      footer = null,
      PaperProps = {},
      showCloseIcon = true,
      closeIcon = (
        <IconButton
          onClick={onClose}
          size="sm"
          hierarchy="tertiaryGrey"
          aria-label="close drawer"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="#667085"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      ),
    },
    ref,
  ) => {
    const isVertical = anchor === "left" || anchor === "right";

    const drawerSx = isVertical
      ? { width: width, flexShrink: 0 }
      : { height: width };

    const paperSx = isVertical
      ? {
          width: width,
          display: "flex",
          flexDirection: "column",
          padding:  "16px 20px",
          gap: "10px",
          minHeight: 56,
          border: "1px solid #D0D5DD",
          boxShadow: "0px 4px 8px -2px #1018281A",
        }
      : {
          height: width,
          display: "flex",
          flexDirection: "column",
          padding:  "16px 20px",
          gap: "10px",
          minHeight: 56,
          border: "1px solid #D0D5DD",
          boxShadow: "0px 4px 8px -2px #1018281A",
        };
    if (!open) return null;
    return (
      <MuiDrawer
        open={open}
        className={`drawer-container ${className}`}
        onClose={onClose}
        anchor={anchor}
        variant={variant}
        PaperProps={{ sx: paperSx, ...PaperProps }}
        ModalProps={{ keepMounted: true }}
      >
        {/* Header */}
        {(headerActions || (closeIcon && showCloseIcon) || title) && (
          <Box
            className="drawer-header-container"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: 36,
            }}
          >
            {title && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {typeof title === "string" ? (
                  <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{
                      textTransform: "capitalize",
                      color: "#101828",
                      fontFamily: "Inter",
                      fontSize: "18px",
                      fontWeight: 500,
                      lineHeight: "28px",
                    }}
                  >
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </Box>
            )}
            {(headerActions || (closeIcon && showCloseIcon)) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {headerActions}
                {closeIcon}
              </Box>
            )}
          </Box>
        )}

        {/* Content */}
        {children && (
          <Box
            sx={{ flex: 1, overflow: "auto", paddingTop: "5px" }}
            className="drawer-content"
          >
            {children}
          </Box>
        )}

        {/* Footer */}
        {footer ? (
          <Box
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            {footer}
          </Box>
        ) : null}
      </MuiDrawer>
    );
  },
);

Drawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  anchor: PropTypes.oneOf(["left", "right", "top", "bottom"]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  variant: PropTypes.oneOf(["temporary", "persistent", "permanent"]),
  children: PropTypes.node,
  items: PropTypes.arrayOf(PropTypes.object),
  onItemClick: PropTypes.func,
  headerActions: PropTypes.node,
  footer: PropTypes.node,
  PaperProps: PropTypes.object,
};

export default Drawer;
